import { useEffect, useRef, useState, type ReactNode } from "react";
import { settingsApi } from "./api";
import { DEFAULT_MANUAL_IMAGE_NEGATIVE_PROMPT, manualImageSubmissionErrorMessage } from "./manual-image";
import type { ManualImagePublicJob } from "./types";
import "./manual-task.css";

interface ManualImageSettingsProps {
  settingsDirty: boolean;
  active?: boolean;
  /** 由父页面提供的图片参数表单，固定占据第三栏。 */
  settingsPanel?: ReactNode;
  /** 从角色题词卡片带来的正负面题词；同一个传送 ID 只应用一次。 */
  promptTransfer?: { id: string; positive_prompt: string; negative_prompt: string } | null | undefined;
  onPresetSaved?: () => void;
  onJobQueued?: (job: ManualImagePublicJob) => void;
}

/** 新建页只负责提交；所有任务状态与结果统一在“任务队列”中查看。 */
export function ManualImageSettings({ settingsDirty, active = true, settingsPanel, promptTransfer, onPresetSaved, onJobQueued }: ManualImageSettingsProps) {
  const [positivePrompt, setPositivePrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState(DEFAULT_MANUAL_IMAGE_NEGATIVE_PROMPT);
  const [pendingAction, setPendingAction] = useState<"save" | "queue" | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const queueRequestRef = useRef<{ id: string; positive: string; negative: string } | null>(null);
  const presetRequestRef = useRef<{ id: string; positive: string; negative: string } | null>(null);
  const appliedTransferRef = useRef<string | null>(null);

  useEffect(() => {
    if (!promptTransfer || promptTransfer.id === appliedTransferRef.current) return;
    appliedTransferRef.current = promptTransfer.id;
    setPositivePrompt(promptTransfer.positive_prompt);
    setNegativePrompt(promptTransfer.negative_prompt);
    setError("");
    setNotice("已填充角色题词；可继续调整后保存或生成。");
  }, [promptTransfer]);

  const pending = pendingAction !== null;

  function validatedPrompts(): { positive: string; negative: string } | null {
    const positive = positivePrompt.trim();
    const negative = negativePrompt.trim();
    if (!positive) { setError("请输入正面提示词。"); return null; }
    if (positive.length > 8_000 || negative.length > 4_000) { setError("题词长度超过服务端限制。"); return null; }
    return { positive, negative };
  }

  async function enqueue(): Promise<void> {
    if (!active || pending) return;
    if (settingsDirty) {
      setError("模型或图片参数尚未自动保存成功，请等待保存完成；若保存失败，请重试后再生图。");
      return;
    }
    const prompts = validatedPrompts();
    if (!prompts) return;

    const previous = queueRequestRef.current;
    const requestId = previous && previous.positive === prompts.positive && previous.negative === prompts.negative
      ? previous.id
      : crypto.randomUUID();
    queueRequestRef.current = { id: requestId, ...prompts };
    setPendingAction("queue");
    setError("");
    setNotice("");
    try {
      const created = await settingsApi.createManualImageJob(requestId, prompts.positive, prompts.negative);
      queueRequestRef.current = null;
      setNotice("已加入队列。请前往“任务队列”查看生成进度和结果。");
      try { onJobQueued?.(created); }
      catch { /* 队列页面的刷新信号失败不影响已完成的入队。 */ }
    } catch (reason) {
      setError(manualImageSubmissionErrorMessage(reason));
    } finally {
      setPendingAction(null);
    }
  }

  async function savePreset(): Promise<void> {
    if (!active || pending) return;
    const prompts = validatedPrompts();
    if (!prompts) return;
    const previous = presetRequestRef.current;
    const requestId = previous && previous.positive === prompts.positive && previous.negative === prompts.negative
      ? previous.id
      : crypto.randomUUID();
    presetRequestRef.current = { id: requestId, ...prompts };
    setPendingAction("save");
    setError("");
    setNotice("");
    try {
      await settingsApi.createImagePromptPreset(requestId, "未命名预设", prompts.positive, prompts.negative);
      presetRequestRef.current = null;
      setNotice("已保存到预设列表。");
      try { onPresetSaved?.(); }
      catch { /* 预设列表刷新失败不影响已保存的预设。 */ }
    } catch (reason) {
      setError(manualImageSubmissionErrorMessage(reason));
    } finally {
      setPendingAction(null);
    }
  }

  return <section className="manual-image-controls" aria-label="新建生图任务">
    <div className="manual-image-task-body">
      <div className="manual-image-task-grid">
        <label>正面提示词<textarea maxLength={8_000} value={positivePrompt} disabled={!active || pending} placeholder="输入希望生成的画面、人物与风格…" onChange={(event) => { setPositivePrompt(event.target.value); setError(""); setNotice(""); }} /></label>
        <label>负面提示词<textarea maxLength={4_000} value={negativePrompt} disabled={!active || pending} placeholder="输入不希望出现的元素（可留空）…" onChange={(event) => { setNegativePrompt(event.target.value); setError(""); setNotice(""); }} /></label>
        <aside className="manual-image-settings-panel" aria-label="图片设置选项">{settingsPanel ?? <p className="panel-state">图片设置正在加载。</p>}</aside>
      </div>
      {settingsDirty ? <p className="manual-image-warning" role="status">正在自动保存修改并同步至服务器...</p> : null}
      {error ? <p className="action-error" role="alert">{error}</p> : null}
      {notice ? <p className="manual-image-queued" role="status">{notice}</p> : null}
    </div>
    <footer className="manual-image-task-footer"><button className="primary manual-image-save" type="button" disabled={!active || pending || !positivePrompt.trim()} onClick={() => void savePreset()}>{pendingAction === "save" ? "添加中…" : "加预设"}</button><button className="primary" type="button" disabled={!active || pending || !positivePrompt.trim() || settingsDirty} onClick={() => void enqueue()}>{pendingAction === "queue" ? "生成中…" : "生成"}</button></footer>
  </section>;
}
